import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { CoverageReport, EngineInput, ValidationMessage, ValidationReport } from '@conciair/scheduler-core';
import { engine } from '../../engine/client';
import { fetchSnapshot, resolveWhere } from '../../engine/snapshot';

/**
 * Live validation and coverage on the current inputs (spec 5.13 and 5.2):
 * one snapshot, validated in the worker after every save. Every input
 * mutation invalidates ['live_checks'].
 */
export interface LiveChecks {
  input: EngineInput;
  hash: string;
  validation: ValidationReport;
  coverage: CoverageReport | null;
}

export function useLiveChecks(orgId: string, enabled = true): UseQueryResult<LiveChecks> {
  return useQuery({
    queryKey: ['live_checks', orgId],
    enabled,
    staleTime: 10_000,
    queryFn: async () => {
      const { input, hash } = await fetchSnapshot(orgId);
      const validation = await engine.validate(input);
      const coverage = validation.errors.length === 0 ? await engine.coverage(input) : null;
      return { input, hash, validation, coverage };
    },
  });
}

export interface Problem {
  level: 'error' | 'warning';
  area: string;
  row: number | null;
  ref: string | null;
  message: string;
  where: string;
}

export interface ProblemIndex {
  all: Problem[];
  byRef: Map<string, Problem[]>;
  byArea: Map<string, Problem[]>;
  errors: number;
  warnings: number;
}

/** V1 area name -> the page that edits it. */
export const AREA_TO_PATH: Record<string, string> = {
  AircraftTypes: '/scheduler/fleet',
  Aircraft: '/scheduler/fleet',
  Pilots: '/scheduler/pilots',
  Qualifications: '/scheduler/pilots',
  Unavailability: '/scheduler/pilots',
  Transitions: '/scheduler/pilots',
  Overrides: '/scheduler/pilots',
  AircraftPairing: '/scheduler/pilots',
  AlternatingPilots: '/scheduler/pilots',
  PreviousRotation: '/scheduler/fleet',
  Config: '/scheduler/settings',
};

export function indexProblems(input: EngineInput | undefined, validation: ValidationReport | undefined): ProblemIndex {
  const all: Problem[] = [];
  const push = (m: ValidationMessage, level: Problem['level']) => {
    const w = input ? resolveWhere(m.where, input) : { area: m.where, row: null, ref: null };
    all.push({ level, area: w.area, row: w.row, ref: w.ref, message: m.message, where: m.where });
  };
  for (const m of validation?.errors ?? []) push(m, 'error');
  for (const m of validation?.warnings ?? []) push(m, 'warning');
  const byRef = new Map<string, Problem[]>();
  const byArea = new Map<string, Problem[]>();
  for (const p of all) {
    if (p.ref) byRef.set(p.ref, [...(byRef.get(p.ref) ?? []), p]);
    byArea.set(p.area, [...(byArea.get(p.area) ?? []), p]);
  }
  return {
    all,
    byRef,
    byArea,
    errors: all.filter((p) => p.level === 'error').length,
    warnings: all.filter((p) => p.level === 'warning').length,
  };
}

export function useProblems(orgId: string, enabled = true): { checks: UseQueryResult<LiveChecks>; problems: ProblemIndex } {
  const checks = useLiveChecks(orgId, enabled);
  const problems = useMemo(() => indexProblems(checks.data?.input, checks.data?.validation), [checks.data]);
  return { checks, problems };
}

/** Problems attached to one record, by its database id. */
export function problemsFor(index: ProblemIndex, ref: string | null | undefined): Problem[] {
  if (!ref) return [];
  return index.byRef.get(ref) ?? [];
}

/** Problems for a set of areas (a page or a pilot section). */
export function problemsForAreas(index: ProblemIndex, areas: string[]): Problem[] {
  return areas.flatMap((a) => index.byArea.get(a) ?? []);
}
